import { useMemo } from 'react';
import { Select, FormField, Input, Alert, Link } from '@cloudscape-design/components';
import { useConfigsByType } from '../hooks/use-configs-by-type';
import { FieldSchema, getTemplate } from '../templates/registry';

interface ReferenceSelectProps {
  field: FieldSchema;
  value: string;
  onChange: (configId: string) => void;
  siblingValues?: Record<string, unknown>;
  error?: string;
}

/**
 * Searchable dropdown for fields that reference other configurations.
 * Fetches configs by type and renders as Cloudscape Select with filtering.
 * Supports context-aware rendering (e.g., E.164 input for phone numbers).
 *
 * Requirements: 2.1–2.16
 */
export function ReferenceSelect({ field, value, onChange, siblingValues, error }: ReferenceSelectProps) {
  // Determine rendering mode from context field
  const contextValue = siblingValues?.[field.contextField ?? ''] as string | undefined;
  const renderMode = field.contextModes?.[contextValue ?? ''] ?? 'dropdown';

  // For E.164 or text mode, render a simple input
  if (renderMode === 'e164') {
    return (
      <FormField label={field.label} description={field.description} errorText={error}>
        <Input
          value={value}
          onChange={({ detail }) => onChange(detail.value)}
          placeholder="+64..."
          type="text"
        />
      </FormField>
    );
  }

  if (renderMode === 'text') {
    return (
      <FormField label={field.label} description={field.description} errorText={error}>
        <Input
          value={value}
          onChange={({ detail }) => onChange(detail.value)}
          placeholder={field.helpText ?? ''}
        />
      </FormField>
    );
  }

  // Dropdown mode — fetch configs by type
  // When multiple referencedConfigTypes exist, select the one matching the context value
  let configType: string | undefined;
  if (field.referencedConfigType) {
    configType = field.referencedConfigType;
  } else if (field.referencedConfigTypes && field.contextField && contextValue) {
    // Match context value (e.g. "menu") to the config type (e.g. "menus")
    // Try exact match first, then plural forms, then prefix match
    configType =
      field.referencedConfigTypes.find((t) => t === contextValue) ??
      field.referencedConfigTypes.find((t) => t === `${contextValue}s` || t === `${contextValue}es` || t === `${contextValue}ions`) ??
      field.referencedConfigTypes.find((t) => t.startsWith(contextValue)) ??
      field.referencedConfigTypes[0];
  } else {
    configType = field.referencedConfigTypes?.[0];
  }
  const { data, isLoading, isError } = useConfigsByType(configType);

  const options = useMemo(() => {
    if (!data?.items) return [];
    // BR-16: Filter out configs that are pending deletion
    return data.items
      .filter((item) => !item.deletionScheduledAt)
      .map((item) => ({
      label: item.configName,
      description: item.configId,
      value: item.configId,
    }));
  }, [data]);

  // Error fallback — render free-text input
  if (isError) {
    return (
      <FormField label={field.label} description={field.description} errorText={error}>
        <Alert type="warning">Failed to load options. Enter value manually.</Alert>
        <Input
          value={value}
          onChange={({ detail }) => onChange(detail.value)}
        />
      </FormField>
    );
  }

  const selectedOption = options.find((o) => o.value === value) ?? null;
  const displayName = configType ? (getTemplate(configType)?.displayName ?? configType) : 'configuration';

  // Warning if pre-existing value not found in options
  const valueNotFound = value && !isLoading && options.length > 0 && !selectedOption;

  return (
    <FormField label={field.label} description={field.description} errorText={error}>
      {valueNotFound && (
        <Alert type="warning">
          ⚠ Referenced configuration '{value}' not found. It may have been deleted.
        </Alert>
      )}
      <Select
        selectedOption={selectedOption}
        onChange={({ detail }) => onChange(detail.selectedOption?.value ?? '')}
        options={options}
        filteringType="auto"
        placeholder={isLoading ? 'Loading...' : `Select a ${displayName}...`}
        loadingText="Loading configurations..."
        statusType={isLoading ? 'loading' : 'finished'}
        empty={
          <>
            No {displayName} configurations found.{' '}
            <Link href={`/configs/new?type=${configType}`}>Create one</Link>
          </>
        }
      />
    </FormField>
  );
}
